import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '../../../base/dialog';
import { getBackstageRoster } from '../../../webinar';
import BackstageDialog from './BackstageDialog';
import BackstageOccupantRow from './BackstageOccupantRow';
import { sortBackstageRoster } from './functions';
import { useModerationStyles } from './styles';
/**
 * How many people the card lists before handing over to the dialog. The Host
 * controls pane is narrow and shared with other cards; a webinar can have
 * dozens waiting backstage.
 */
const MAX_INLINE_ROWS = 4;
/**
 * The people currently waiting backstage, shown to main-room moderators inside
 * the Host controls backstage card, each with Bring live and the other
 * per-person controls. Past a few people it shows the first few and a link to
 * the full backstage dialog, where the whole list is searchable.
 *
 * The main room has no visibility into the backstage room, so this list comes
 * from the server (mod_webinar_roles publishes it to moderators only) rather
 * than from the local participant state.
 *
 * @returns {JSX.Element | null}
 */
const BackstageRoster = () => {
    const { t } = useTranslation();
    const { classes } = useModerationStyles();
    const dispatch = useDispatch();
    const roster = useSelector(getBackstageRoster);
    const [expandedId, setExpandedId] = useState();
    const onToggleActions = useCallback((id) => setExpandedId(current => (current === id ? undefined : id)), []);
    const onAction = useCallback(() => setExpandedId(undefined), []);
    const openAll = useCallback(() => dispatch(openDialog(BackstageDialog)), [dispatch]);
    if (!roster.length) {
        return null;
    }
    const occupants = sortBackstageRoster(roster);
    const shown = occupants.slice(0, MAX_INLINE_ROWS);
    const hasMore = occupants.length > MAX_INLINE_ROWS;
    return (React.createElement("div", { className: classes.roster },
        React.createElement("div", { className: classes.rosterHeader },
            React.createElement("span", { className: classes.rosterTitle }, t('webinar.backstageWaiting', { count: occupants.length })),
            React.createElement("button", { className: classes.rosterLink, onClick: openAll, type: 'button' }, t('webinar.backstageViewAll', { count: occupants.length }))),
        shown.map(occupant => (React.createElement(BackstageOccupantRow, { expanded: expandedId === occupant.id, key: occupant.id, occupant: occupant, onAction: onAction, onToggleActions: onToggleActions }))),
        hasMore && (React.createElement("button", { className: classes.rosterMore, onClick: openAll, type: 'button' }, t('webinar.backstageMore', { count: occupants.length - MAX_INLINE_ROWS })))));
};
export default BackstageRoster;
